import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  Card,
  CardContent,
  Stack,
  Skeleton,
} from '@mui/material'
import PageHelpButton from '../../components/PageHelpButton'
import DiscardChangesDialog from '../../components/common/DiscardChangesDialog'
import PortalUnavailable from '../../components/portal/PortalUnavailable'
import { usePortalData } from '../../components/portal/PortalDataProvider'
import { clientsApi, Client } from '../../services/api'
import ClientResumeUploadTab from '../../components/client-detail/ClientResumeUploadTab'
import DreamJobForm from '../../components/client-detail/DreamJobForm'
import CliftonStrengthsForm from '../../components/client-detail/CliftonStrengthsForm'
import { REQUIRED_CLIFTON_STRENGTHS } from '../../utils/cliftonStrengths'
import PortalResume from './PortalResume'
import PortalLinkedIn from './PortalLinkedIn'
import PortalNorthStar from './PortalNorthStar'
import PortalTasks from './PortalTasks'
import PortalDocuments from './PortalDocuments'

interface TabPanelProps {
  children?: React.ReactNode
  slug: string
  value: string
  /** Whether a tab bar is on screen to label this panel. */
  tabbed: boolean
}

function TabPanel(props: TabPanelProps) {
  const { children, value, slug, tabbed, ...other } = props

  // With a single visible tab the bar is not rendered, so there is no tab to
  // point at: keeping the tabpanel role and aria-labelledby would name an id
  // that is not in the document. Unlabelled, it is simply the card's content.
  return (
    <div
      role={tabbed ? 'tabpanel' : undefined}
      hidden={value !== slug}
      id={`profile-tabpanel-${slug}`}
      aria-labelledby={tabbed ? `profile-tab-${slug}` : undefined}
      {...other}
    >
      {value === slug && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(slug: string) {
  return {
    id: `profile-tab-${slug}`,
    'aria-controls': `profile-tabpanel-${slug}`,
  }
}

// The tabs, keyed on the slug the URL already carries rather than on a position
// in the list: a stage can hide either one, and an index would then have to be
// kept in step in four places at once.
// Ordered to match the staff client record: profile, then the deliverables, then
// the work, then the files. Each label is the one stageVisibility already gives
// the entry, so the portal and the Stage Gates admin call the same thing by the
// same name.
const PROFILE_TABS = [
  { slug: 'dreamjob', entryId: 'dream-job', label: 'Dream Job' },
  { slug: 'clifton-strengths', entryId: 'clifton-strengths', label: 'Clifton Strengths' },
  { slug: 'resume', entryId: 'resume-old', label: 'Resume' },
  { slug: 'my-resume', entryId: 'resume-new', label: 'My Resume' },
  { slug: 'linkedin', entryId: 'linkedin', label: 'LinkedIn' },
  { slug: 'north-star', entryId: 'north-star', label: 'North Star' },
  { slug: 'tasks', entryId: 'tasks', label: 'Tasks' },
  { slug: 'documents', entryId: 'client-folder', label: 'Documents' },
] as const

export default function PortalProfile() {
  const { clientId, visibility, dashboard, refresh } = usePortalData()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [client, setClient] = useState<Client | null>(null)
  // The tab panels unmount when another tab is chosen, which would throw away a
  // Dream Job edit nothing has saved, so a switch away from it asks first.
  const dreamJobDirtyRef = useRef(false)
  const handleDreamJobDirty = useCallback((dirty: boolean) => {
    dreamJobDirtyRef.current = dirty
  }, [])
  const [pendingTab, setPendingTab] = useState<string | null>(null)

  const visibleTabs = useMemo(
    () => PROFILE_TABS.filter((tab) => visibility.isEntryVisible(tab.entryId)),
    [visibility]
  )
  const requestedTab = searchParams.get('tab') || ''
  const activeTab = visibleTabs.find((tab) => tab.slug === requestedTab) ?? visibleTabs[0]

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const clientData = await clientsApi.get(clientId)
        setClient(clientData)
      } catch (err) {
        console.error('Failed to load client:', err)
        setError('Failed to load your profile')
      } finally {
        setLoading(false)
      }
    }

    fetchClient()
  }, [clientId])

  // A deep link can name a tab this stage hides. Rewriting the param rather
  // than quietly ignoring it keeps browser back and forward from resurrecting
  // the hidden tab.
  useEffect(() => {
    if (activeTab && requestedTab && requestedTab !== activeTab.slug) {
      setSearchParams({ tab: activeTab.slug }, { replace: true })
    }
  }, [activeTab, requestedTab, setSearchParams])

  const handleTabChange = (_: React.SyntheticEvent, slug: string) => {
    if (activeTab?.slug === 'dreamjob' && slug !== 'dreamjob' && dreamJobDirtyRef.current) {
      setPendingTab(slug)
      return
    }
    setSearchParams({ tab: slug })
  }

  const discardDreamJobEdits = () => {
    if (pendingTab) {
      dreamJobDirtyRef.current = false
      setSearchParams({ tab: pendingTab })
    }
    setPendingTab(null)
  }

  const handleClientUpdate = (updatedData: Partial<Client>) => {
    if (client) {
      setClient({ ...client, ...updatedData })
    }
    // The dashboard's progress and checklist read the completion stamp the
    // server sets on reaching the fifth strength, and the portal fetches the
    // dashboard once per session, so it is re-read when that stamp is new.
    if (
      updatedData.cliftonStrengths &&
      updatedData.cliftonStrengths.length >= REQUIRED_CLIFTON_STRENGTHS &&
      dashboard?.formCompletion.cliftonStrengths.completed === false
    ) {
      void refresh()
    }
  }

  // The layout gate already answers this URL when both tabs are hidden; the
  // page says the same thing standalone rather than rendering a bare card.
  if (!activeTab) {
    return <PortalUnavailable />
  }

  // The page's own shape rather than a centred spinner, which threw the
  // heading away and rebuilt it on every load.
  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={26} />
        <Skeleton variant="text" width={300} height={20} sx={{ mb: 3 }} />
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={160} sx={{ borderRadius: '12px' }} />
          <Skeleton variant="rounded" height={220} sx={{ borderRadius: '12px' }} />
        </Stack>
      </Box>
    )
  }

  if (error || !client) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error || 'Unable to load profile'}
      </Alert>
    )
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h4" component="h1">
          My Profile
        </Typography>
        <PageHelpButton articleId="page-portal-profile" />
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Complete your profile to help us create the perfect resume for your job search.
      </Typography>

      <Card>
        {/* One tab is no choice, and a lone tab — or a disabled second one —
            would tell the client a section exists that the stage hides. */}
        {visibleTabs.length > 1 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab.slug}
              onChange={handleTabChange}
              aria-label="profile tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {visibleTabs.map((tab) => (
                <Tab key={tab.slug} value={tab.slug} label={tab.label} {...a11yProps(tab.slug)} />
              ))}
            </Tabs>
          </Box>
        )}
        <CardContent>
          {visibleTabs.map((tab) => (
            <TabPanel
              key={tab.slug}
              value={activeTab.slug}
              slug={tab.slug}
              tabbed={visibleTabs.length > 1}
            >
              {/* Each section is the page that used to own it, mounted here
                  with its heading suppressed. Nothing about what they read or
                  write moved — in particular every entityType is still the one
                  its own page passed, which is what the portal's document
                  routing keys off. */}
              {tab.slug === 'dreamjob' && (
                <DreamJobForm
                  client={client}
                  onUpdate={handleClientUpdate}
                  onDirtyChange={handleDreamJobDirty}
                />
              )}
              {tab.slug === 'clifton-strengths' && (
                <CliftonStrengthsForm client={client} onUpdate={handleClientUpdate} isPortal />
              )}
              {tab.slug === 'resume' && (
                <ClientResumeUploadTab client={client} onUpdate={handleClientUpdate} isPortal />
              )}
              {tab.slug === 'my-resume' && <PortalResume embedded />}
              {tab.slug === 'linkedin' && <PortalLinkedIn embedded />}
              {tab.slug === 'north-star' && <PortalNorthStar embedded />}
              {tab.slug === 'tasks' && <PortalTasks embedded />}
              {tab.slug === 'documents' && <PortalDocuments embedded />}
            </TabPanel>
          ))}
        </CardContent>
      </Card>

      <DiscardChangesDialog
        open={pendingTab !== null}
        title="Leave without saving?"
        message="You have changes to your Dream Job that are not saved. If you leave this tab they will be lost."
        onKeepEditing={() => setPendingTab(null)}
        onDiscard={discardDreamJobEdits}
      />
    </Box>
  )
}
